/**
 * F17 T17.1.5 — EndNode: węzeł końcowy procesu.
 *
 * Proces kończy się na NAZWANYM etapie końcowym (model WF): tło węzła jest neutralne, a to,
 * jak sprawa się zakończyła, mówi jego nazwa (`data.label`, np. „Zatwierdzony", „Odrzucony").
 * Pusta nazwa → domyślny tekst „Koniec" (publikacja i tak wymaga nazwy). Min. 1 end node per
 * graf (walidacja publish). Handle: target top.
 *
 * `data.isCurrent` (F17 T17.1.8c) — podświetlenie w read-only `ProcessGraphViewer`. Builder
 * nigdy tego pola nie ustawia (patrz `graphTypes.ts`), więc render w builderze bez zmian.
 */

import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Handle, type NodeProps, Position } from '@xyflow/react';
import { Flag } from 'lucide-react';

import { CURRENT_NODE_RING_CLASS } from '@/lib/processes/step-tone';

interface EndNodeData {
  label?: string;
  isCurrent?: boolean;
  /** Znacznik diagramu sprawy (`CaseNodeData.caseMarker`) — `final` = sprawa zakończyła się tu. */
  caseMarker?: string;
  [key: string]: unknown;
}

export const EndNode = memo(function EndNode({ data, selected }: NodeProps) {
  const { t } = useTranslation();
  const nodeData = (data as EndNodeData) ?? {};
  const name = nodeData.label?.trim() ?? '';
  const isCurrent = nodeData.isCurrent ?? false;
  // Etap końcowy zakończonej sprawy dostaje `isCurrent` tylko dla podświetlenia; czytnik ekranu
  // ma już widoczną plakietkę „Sprawa zakończona", więc „Bieżący krok" byłoby sprzeczne.
  const announcesCurrentStep = isCurrent && nodeData.caseMarker !== 'final';

  return (
    <div
      className={`flex min-w-[96px] max-w-[220px] items-center justify-center gap-2 rounded-full bg-slate-600 px-4 py-2.5 shadow-lg shadow-slate-500/20 transition-shadow dark:bg-slate-500 ${
        selected ? 'ring-2 ring-accent-500 ring-offset-2 dark:ring-offset-gray-900' : ''
      } ${isCurrent ? CURRENT_NODE_RING_CLASS : ''}`}
      data-testid="process-node-end"
      data-current={isCurrent ? 'true' : 'false'}
    >
      <Flag className="h-4 w-4 shrink-0 text-white" aria-hidden="true" />
      <span
        className={`truncate text-sm font-semibold text-white ${name ? '' : 'italic opacity-80'}`}
        data-testid="process-node-end-name"
        title={name || undefined}
      >
        {name || t('tenant.processes.builder.nodes.endDefaultLabel')}
      </span>
      {announcesCurrentStep && (
        <span className="sr-only">{t('tenant.processes.flow.currentStepAria')}</span>
      )}
      <Handle
        type="target"
        position={Position.Top}
        className="!h-3 !w-3 !border-2 !border-white !bg-slate-700"
      />
    </div>
  );
});
